if (!customElements.get("drawer-animation")) {
  class DrawerAnimation extends HTMLElement {
    constructor() {
      super();

      this.selectors = {
        group: "[data-drawer-group]",
        tool: ".sk-drawer-animation__tool",
        trigger: "[data-tool-trigger]",
        card: "[data-tool-card]",
        close: "[data-card-close]",
        modal: "[data-tool-modal]",
      };
    }

    connectedCallback() {
      this.group = this.querySelector(this.selectors.group);

      this.travel = parseFloat(this.dataset.travel);
      if (isNaN(this.travel)) this.travel = 85;

      // Where down the viewport the section's top edge sits when the drawer
      // starts opening. 100% is the very bottom of the screen, so 95% holds the
      // drawer closed across the bottom 5% of the screen before it opens.
      this.startAt = parseFloat(this.dataset.start);
      if (isNaN(this.startAt)) this.startAt = 95;

      // Where down the viewport the section's MIDDLE sits when the drawer is
      // fully open. 50% is the section centred; higher finishes sooner. The
      // middle (not the bottom edge) is used so an early finish still keeps
      // its marker on screen, where it can be tuned.
      this.endAt = parseFloat(this.dataset.end);
      if (isNaN(this.endAt)) this.endAt = 70;

      // Desktop only — mobile is a single flat background with no movement.
      if (!window.FoxThemeSettings || !window.FoxThemeSettings.isMobile) {
        this.initScroll();
      }

      this.unmatchMobileHandler = this.onUnmatchMobile.bind(this);
      this.matchMobileHandler = this.onMatchMobile.bind(this);
      document.addEventListener("unmatchMobile", this.unmatchMobileHandler);
      document.addEventListener("matchMobile", this.matchMobileHandler);

      // Cards are independent of the scroll animation — they must keep working
      // when reduced motion or a missing GSAP short-circuits initScroll.
      this.initCards();
      this.collectModals();
    }

    disconnectedCallback() {
      document.removeEventListener("unmatchMobile", this.unmatchMobileHandler);
      document.removeEventListener("matchMobile", this.matchMobileHandler);

      this.removeEventListener("click", this.clickHandler);
      document.removeEventListener("keyup", this.keyupHandler);
      document.removeEventListener("pointerdown", this.outsideHandler);

      // The modal lives in <body>, outside this element, so it would outlive
      // an editor re-render with the page still scroll-locked.
      this.closeModal();
      this.destroyScroll();
    }

    // Whatever is open belongs to the layout being left, so close it on the
    // way across the breakpoint.
    onUnmatchMobile() {
      this.closeModal();
      this.initScroll();
    }

    onMatchMobile() {
      this.closeCard(false);
      this.destroyScroll();
    }

    /* Scroll animation ---------------------------------------------------- */

    initScroll() {
      if (!this.group) return;
      if (this.timeline) return;
      if (window.FoxThemeSettings && window.FoxThemeSettings.motionReduced) return;
      if (typeof gsap === "undefined" || typeof ScrollTrigger === "undefined") return;

      gsap.registerPlugin(ScrollTrigger);

      // The drawer sits low in the scene, so the scene's first visible pixels
      // are the floor, not the drawer. Starting at "top bottom" would burn most
      // of the scroll range before the drawer is even on screen — the closed
      // state would never be seen. Start once the section is properly in view
      // instead, and finish once its middle reaches the "Fully open at" point.
      //
      // The scroll range works out to half the section's height plus
      // (start - end)% of the screen. The drawer travels `travel`% of the
      // section's height; if the range is smaller than that, the drawer moves
      // faster than the page and its bottom runs off the screen. Keeping
      // Start high (95) and End no higher than ~70 avoids it on common
      // desktop sizes.
      // The tools live inside the group, so they ride along with the drawer.
      // The wrapper (not this element) carries the open/closed state, because
      // the header with the subtitle caret is a sibling of the scene, not a
      // child. The caret is flipped while the drawer is closed and turns back
      // once it is fully open. Wherever the drawer doesn't animate (mobile,
      // reduced motion, no GSAP) the class is never added, so the caret sits
      // in its open position — matching the drawer, which rests open.
      this.stateEl = this.closest(".sk-drawer-animation") || this;
      this.stateEl.classList.add("is-drawer-closed");

      this.timeline = gsap.timeline({
        scrollTrigger: {
          trigger: this,
          start: "top " + this.startAt + "%",
          end: "center " + this.endAt + "%",
          scrub: 1,
          invalidateOnRefresh: true,
          // Debug only: the section's "Show animation markers" setting shows
          // where the animation starts and ends (green = start, red = end).
          markers: this.hasAttribute("data-markers"),
        },
        // Follows the drawer's actual position, which trails the scroll by
        // the scrub smoothing. That smoothing eases out, so the last few
        // percent take most of its ~1s — waiting for exactly 1 made the caret
        // lag well behind a drawer that already looked open. 0.95 is
        // visually open; raise it for a later turn, lower it for earlier.
        onUpdate: () => {
          this.stateEl.classList.toggle(
            "is-drawer-closed",
            this.timeline.progress() < 0.95
          );
        },
      });

      this.timeline.fromTo(
        this.group,
        { yPercent: -this.travel },
        { yPercent: 0, ease: "none" }
      );

      // Art-directed <picture> sources can settle after first layout; recompute
      // start/end once everything has loaded so "centre screen" is accurate.
      if (document.readyState !== "complete") {
        window.addEventListener("load", () => ScrollTrigger.refresh(), { once: true });
      }
    }

    destroyScroll() {
      if (!this.timeline) return;

      // Scoped teardown — only this instance's trigger, never ScrollTrigger.getAll().
      if (this.timeline.scrollTrigger) this.timeline.scrollTrigger.kill();
      this.timeline.kill();
      this.timeline = null;

      gsap.set(this.group, { clearProps: "transform" });
      // The drawer rests open without the animation, so the caret should too.
      if (this.stateEl) this.stateEl.classList.remove("is-drawer-closed");
    }

    /* Tool cards ---------------------------------------------------------- */

    initCards() {
      if (!this.querySelector(this.selectors.trigger)) return;

      // Keyed by block id, the same way as the modals.
      this.cards = {};
      this.querySelectorAll(this.selectors.card).forEach((card) => {
        this.cards[card.dataset.toolCard] = card;
      });

      // Bound once each, so they can actually be removed on disconnect and
      // cannot stack up across repeated opens.
      this.clickHandler = this.onClick.bind(this);
      this.keyupHandler = this.onKeyUp.bind(this);
      this.outsideHandler = this.onPointerDownOutside.bind(this);

      this.addEventListener("click", this.clickHandler);
      // On the document, not the element: Safari doesn't focus a button when
      // it is clicked, so after a mouse open focus is still on <body> and an
      // element-level listener would never hear Escape.
      document.addEventListener("keyup", this.keyupHandler);
      document.addEventListener("pointerdown", this.outsideHandler);
    }

    onClick(event) {
      if (event.target.closest(this.selectors.close)) {
        this.closeCard(true);
        return;
      }

      const trigger = event.target.closest(this.selectors.trigger);
      if (!trigger) return;

      if (window.FoxThemeSettings && window.FoxThemeSettings.isMobile) {
        this.openModal(trigger);
        return;
      }

      if (trigger.getAttribute("aria-expanded") === "true") {
        this.closeCard(false);
        return;
      }
      this.requestOpen(trigger);
    }

    // event.key rather than event.code: Chrome fires keyup with no code for
    // autofill, and code.toUpperCase() would throw.
    onKeyUp(event) {
      if (event.key !== "Escape") return;
      this.closeCard(true);
    }

    // "Outside" is anywhere but a card or a tool trigger — including empty
    // parts of the scene, so clicking the drawer art also dismisses. Triggers
    // are left to onClick, which closes the current card on its own.
    onPointerDownOutside(event) {
      // A queued open counts too, so clicking away also cancels a card that
      // was about to appear.
      if (!this.openTrigger && !this.pendingTrigger) return;
      const inside = event.target.closest(
        this.selectors.card + ", " + this.selectors.trigger
      );
      if (inside && this.contains(inside)) return;
      this.closeCard(false);
    }

    // Opens a card only once any card that is closing has fully finished.
    // Overlapping the two would put both cards at z-index 4 and both tools at
    // 5 for the length of the animation, so they fight over the stacking.
    //
    // pendingTrigger is "the latest request wins": if another tool is clicked
    // while we wait, the earlier queued open is dropped rather than both
    // opening once the close resolves.
    requestOpen(trigger) {
      const closing = this.closeCard(false);
      this.pendingTrigger = trigger;

      closing.then(() => {
        if (this.pendingTrigger !== trigger) return;
        this.pendingTrigger = null;
        this.openCard(trigger);
      });
    }

    openCard(trigger) {
      const card = this.cards[trigger.dataset.toolTrigger];
      if (!card) return;

      // Visual and accessible state always move together.
      card.hidden = false;
      card.classList.add("is-open");
      trigger.setAttribute("aria-controls", card.id);
      trigger.setAttribute("aria-expanded", "true");

      // Lifts this tool above the open card; the card is already above every
      // other tool. Stacking order is defined in the SCSS, not here.
      const tool = trigger.closest(this.selectors.tool);
      if (tool) tool.classList.add("is-active");

      this.openCardEl = card;
      this.openTrigger = trigger;
      this.openTool = tool;
    }

    // Returns a promise that settles once no card is closing any more, so
    // requestOpen can wait on it. Resolves straight away when there was
    // nothing to close.
    closeCard(returnFocus) {
      // Any explicit close also cancels an open that was queued behind one.
      this.pendingTrigger = null;

      if (!this.openTrigger) return this.closing || Promise.resolve();

      const card = this.openCardEl;
      const trigger = this.openTrigger;
      const tool = this.openTool;

      // Accessible state flips immediately; only the visual exit is deferred.
      trigger.setAttribute("aria-expanded", "false");
      this.openCardEl = null;
      this.openTrigger = null;
      this.openTool = null;

      const closing = this.animateOut(card, tool);
      this.closing = closing;
      closing.then(() => {
        if (this.closing === closing) this.closing = null;
      });

      if (returnFocus) trigger.focus();
      return closing;
    }

    // Plays the open keyframe in reverse, then hides, and resolves once the
    // card is hidden. The card has to stay displayed until the animation ends —
    // setting `hidden` first is what made it vanish instantly before.
    //
    // A close is never cut short: requestOpen always waits for it, so at most
    // one card is closing at a time and none is reopened mid-close. That is
    // what lets this be a plain promise with no cancellation bookkeeping.
    animateOut(card, tool) {
      card.classList.remove("is-open");
      // Forces a style flush between the two classes. They share an
      // animation-name, so swapping them directly would only flip the
      // direction of an animation that has already finished — the card would
      // snap shut instead of animating.
      void card.offsetWidth;
      card.classList.add("is-closing");

      // With reduced motion nothing is running, so this resolves at once. The
      // timer covers a backgrounded tab, where `finished` can stall; it is
      // comfortably longer than the 0.2s duration.
      const finished = Promise.all(
        card.getAnimations().map((animation) => animation.finished)
      ).catch(() => {});
      const timeout = new Promise((resolve) => setTimeout(resolve, 500));

      return Promise.race([finished, timeout]).then(() => {
        card.hidden = true;
        card.classList.remove("is-closing");
        // The tool stays lifted while its card collapses behind it.
        if (tool) tool.classList.remove("is-active");
      });
    }

    /* Mobile modals ------------------------------------------------------- */

    // jQuery Modal moves each modal to <body> on first open and leaves it
    // there, so grab references now, while they are still inside the section,
    // and never look them up by selector again.
    collectModals() {
      // Kept across reconnects: if the editor moves this element rather than
      // re-rendering it, modals already in <body> won't be found below.
      this.modals = this.modals || {};

      this.querySelectorAll(this.selectors.modal).forEach((modal) => {
        // A theme editor re-render leaves the previous copy in <body>; drop
        // it so there is only ever one element with this id.
        document.querySelectorAll('[id="' + modal.id + '"]').forEach((el) => {
          if (el !== modal) el.remove();
        });
        this.modals[modal.dataset.toolModal] = modal;
      });
    }

    openModal(trigger) {
      const modal = this.modals && this.modals[trigger.dataset.toolTrigger];
      // plugins.min.js is deferred and loads after this file, so check for
      // the plugin at tap time rather than at connect.
      const $ = window.jQuery;
      if (!modal || !$ || !$.modal) return;

      // The markup points at the desktop card; on mobile this opens the modal.
      trigger.setAttribute("aria-controls", modal.id);
      trigger.setAttribute("aria-expanded", "true");
      this.modalTrigger = trigger;

      // The plugin never returns focus, so do it here.
      $(modal).one("modal:after-close", () => {
        trigger.setAttribute("aria-expanded", "false");
        if (this.modalTrigger === trigger) this.modalTrigger = null;
        trigger.focus();
      });

      // With a fade the plugin only displays the modal after a delay, so focus
      // has to wait for its open event — focusing a hidden element is a no-op.
      $(modal).one("modal:open", () => {
        const close = modal.querySelector(".sk-drawer-modal__close");
        if (close) close.focus();
      });

      const motionReduced =
        window.FoxThemeSettings && window.FoxThemeSettings.motionReduced;

      $(modal).modal({
        // null turns the plugin's fade off, matching the cards' reduced-motion
        // behaviour.
        fadeDuration: motionReduced ? null : 200,
        showClose: false,
        blockerClass: "jquery-modal sk-drawer-modal-blocker",
      });
    }

    closeModal() {
      const $ = window.jQuery;
      if (!this.modalTrigger || !$ || !$.modal) return;
      $.modal.close();
    }
  }

  customElements.define("drawer-animation", DrawerAnimation);
}
